import type { ScenarioConfig, SimResult } from "@air-defence-sim/sim-core";

/** Earliest detection time in the run, or null if nothing was ever detected. */
export function firstDetectionTime(r: SimResult): number | null {
  let first: number | null = null;
  for (const e of r.events) {
    if (e.type === "detect" && (first === null || e.t < first)) first = e.t;
  }
  return first;
}

const pct = (x: number): string => `${Math.round(x * 100)}%`;

/** Build the "Reading this run" commentary for the allocation sim. */
export function buildRunAnalysis(cfg: ScenarioConfig, r: SimResult): string {
  const m = r.metrics;
  const preserved = m.initialTargetValue > 0 ? m.remainingTargetValue / m.initialTargetValue : 0;
  const totalWeapons = r.waves.reduce((s, w) => s + w.weapons.length, 0);
  const mobilePct = Math.round(cfg.mobileDefenceBudgetShare * 100);
  const magHot = Math.max(m.fixedDefenceUtilisation, m.mobileDefenceUtilisation);
  const hotSide =
    m.fixedDefenceUtilisation >= m.mobileDefenceUtilisation ? "fixed batteries" : "CAP fighters";

  const parts: string[] = [];
  parts.push(`<div class="figure"><b>The allocation trade.</b> A defence budget of
    <b>${cfg.totalDefenceBudget}</b>, split <b>${mobilePct}% mobile / ${100 - mobilePct}% fixed</b>,
    faced <b>${totalWeapons}</b> weapons in <b>${cfg.attackerWaveCount}</b>
    wave${cfg.attackerWaveCount === 1 ? "" : "s"} at concentration
    <b>${cfg.attackerConcentration.toFixed(2)}</b>. <b>${pct(preserved)}</b> of target value survived.
    ${
      magHot > 0.85
        ? `The ${hotSide} ran their magazines to <b>${pct(magHot)}</b> utilisation — this defence is one
           wave away from saturation, and every weapon past the magazine ceiling is a guaranteed leaker.`
        : `Peak magazine utilisation was <b>${pct(magHot)}</b> (${hotSide}) — the defence still had shots
           in reserve when the attack ended.`
    }</div>`);
  parts.push(`<p>This run: <span class="k">${totalWeapons} launched</span> &rarr;
    <span class="k">${m.fixedDefenceShotsFired} fixed shots</span> +
    <span class="k">${m.mobileDefenceShotsFired} mobile shots</span>,
    <span class="k">${m.leakers} leaker${m.leakers === 1 ? "" : "s"}</span>, destroying
    <span class="k">${m.targetsDestroyed} target${m.targetsDestroyed === 1 ? "" : "s"}</span>.
    Mean warning time was <span class="k">${m.warningTimeMean.toFixed(1)} s</span> —
    ${
      cfg.warningRangeMultiplier < 1
        ? "with warning this shallow, mobile defence cannot reposition before weapons arrive; whatever sits at the point of attack is the defence."
        : "enough for the mobile layer to concentrate toward the threatened axis, which is exactly the behaviour the attacker's dispersion is trying to defeat."
    }
    ${
      m.concentrationEfficiency < 0.5
        ? `Concentration efficiency came out at <span class="k">${pct(m.concentrationEfficiency)}</span>:
           the attacker successfully massed more weapons than the local defence could service.`
        : `Concentration efficiency of <span class="k">${pct(m.concentrationEfficiency)}</span> means the
           value the attacker actually aimed at mostly survived.`
    }</p>`);
  return parts.join("");
}

/**
 * Build the counterfactual figure. `cf` is the same seed and attack re-run with
 * `cfShare` mobile budget share instead of the user's allocation.
 */
export function buildCounterfactual(current: SimResult, cf: SimResult, cfShare: number): string {
  const cur =
    current.metrics.initialTargetValue > 0
      ? current.metrics.remainingTargetValue / current.metrics.initialTargetValue
      : 0;
  const alt =
    cf.metrics.initialTargetValue > 0
      ? cf.metrics.remainingTargetValue / cf.metrics.initialTargetValue
      : 0;
  const deltaPts = Math.round((cur - alt) * 100);
  const cfLabel = cfShare === 0 ? "an all-fixed allocation" : `a ${Math.round(cfShare * 100)}% mobile allocation`;
  const verdict =
    deltaPts > 0
      ? `Your allocation preserved <b>${Math.abs(deltaPts)} percentage points more value</b> from the same
         attack, same dice — ${cfShare === 0 ? "the mobile layer is earning its keep against this attack pattern" : "fixed mass at the point of attack beat flexibility this time"}.`
      : deltaPts < 0
        ? `The alternative preserved <b>${Math.abs(deltaPts)} percentage points more value</b> from the same
           attack, same dice — ${cfShare === 0 ? "this attack rewarded fixed mass at the defended points over flexibility" : "this attack pattern punished a static defence; mobility would have paid"}.`
        : "Both allocations preserved the same value against this attack — push concentration or wave count and re-run.";
  return `<b>Counterfactual, same seed:</b> replaying this exact attack with ${cfLabel} gives
    <b>${pct(alt)}</b> of target value preserved (vs <b>${pct(cur)}</b> with yours), with
    <b>${cf.metrics.leakers}</b> leaker${cf.metrics.leakers === 1 ? "" : "s"}
    (vs ${current.metrics.leakers}). ${verdict}`;
}
